import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Crown, MessageSquare, RefreshCw, Loader2, Check, X } from "lucide-react";

export const SupportersPanel = () => {
  const [rows, setRows] = useState(null);
  const [syncing, setSyncing] = useState("");

  const load = () => api.get("/admin/supporters").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const syncRole = async (uid) => {
    setSyncing(uid);
    try {
      const { data } = await api.post(`/admin/supporters/${uid}/sync-role`);
      toast.success(data.ok ? "Discord role synced." : "Couldn't update the role — is the member in your server?");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed.");
    } finally {
      setSyncing("");
    }
  };

  return (
    <div className="mt-8 rounded-2xl border border-[#1E293F] bg-[#0F1424] p-6" data-testid="supporters-panel">
      <div className="flex items-center gap-2">
        <Crown className="text-[#FFB800]" size={20} />
        <h2 className="font-heading text-xl font-bold text-white">Supporters</h2>
        <button onClick={load} data-testid="supporters-refresh" className="ml-auto text-[#64748B] transition-colors hover:text-white"><RefreshCw size={16} /></button>
      </div>
      <p className="mt-1 text-sm text-[#94A3B8]">Everyone who paid, and whether their Discord is linked / the Verified Supporter role is granted.</p>

      {rows === null ? (
        <div className="flex justify-center py-8 text-[#64748B]"><Loader2 className="animate-spin" /></div>
      ) : rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-[#64748B]" data-testid="supporters-empty">No supporters yet.</p>
      ) : (
        <div className="mt-4 space-y-2">
          {rows.map((r) => (
            <div key={r.user_id} data-testid={`supporter-row-${r.user_id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-[#1E293F] bg-[#141B2D] p-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold text-white">{r.name}</span>
                  {r.is_supporter
                    ? <span className="inline-flex items-center gap-1 rounded-full bg-[#FFB800]/15 px-2 py-0.5 text-[10px] font-bold uppercase text-[#FFB800]"><Crown size={10} /> Supporter</span>
                    : <span className="rounded-full bg-[#64748B]/15 px-2 py-0.5 text-[10px] font-bold uppercase text-[#64748B]">Free</span>}
                </div>
                <div className="truncate text-xs text-[#64748B]">{r.email}{r.supporter_plan ? ` · ${r.supporter_plan}` : ""}</div>
              </div>
              <div className="flex items-center gap-2 text-xs">
                {r.discord_id ? (
                  <span className="inline-flex items-center gap-1 rounded-md bg-[#5865F2]/15 px-2 py-1 font-semibold text-[#5865F2]"><MessageSquare size={12} /> @{r.discord_username}</span>
                ) : (
                  <span className="rounded-md bg-[#0F1424] px-2 py-1 text-[#64748B]">No Discord</span>
                )}
                {r.discord_id && (
                  <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 font-semibold ${r.discord_role_granted ? "bg-[#00FF87]/15 text-[#00FF87]" : "bg-[#FF2A55]/10 text-[#FF2A55]"}`}>
                    {r.discord_role_granted ? <Check size={12} /> : <X size={12} />} Role
                  </span>
                )}
              </div>
              {r.discord_id && (
                <Button data-testid={`supporter-sync-${r.user_id}`} size="sm" disabled={syncing === r.user_id} onClick={() => syncRole(r.user_id)}
                  className="border border-[#5865F2]/40 bg-[#5865F2]/10 text-[#5865F2] hover:bg-[#5865F2]/20">
                  {syncing === r.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw size={13} />}
                  <span className="ml-1">Sync</span>
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
